import Phaser from 'phaser';
import { GAME_HEIGHT } from '../data/gameConstants';
import { InputSystem } from '../systems/InputSystem';
import { ResponsiveSystem } from '../systems/ResponsiveSystem';
import { UIStyle } from './UIStyle';

export class MobileControls {
  private scene: Phaser.Scene;
  private inputSystem: InputSystem;
  private joystickZone?: Phaser.GameObjects.Zone;
  private joystickBase?: Phaser.GameObjects.Arc;
  private joystickThumb?: Phaser.GameObjects.Arc;
  private actionButton?: Phaser.GameObjects.Arc;
  private actionLabel?: Phaser.GameObjects.Text;
  private joystickCenter = { x: 110, y: GAME_HEIGHT - 110 };
  private joystickRadius = 58;
  private thumbRadius = 26;
  private pointerId: number | null = null;
  private layout = ResponsiveSystem.getTouchControlLayout();
  private moveHandler?: (pointer: Phaser.Input.Pointer) => void;
  private upHandler?: (pointer: Phaser.Input.Pointer) => void;
  private unbindResize?: () => void;
  private controlsVisible = true;

  constructor(scene: Phaser.Scene, inputSystem: InputSystem) {
    this.scene = scene;
    this.inputSystem = inputSystem;
  }

  create(): void {
    if (!ResponsiveSystem.shouldShowTouchControls(this.scene)) return;

    this.layout = ResponsiveSystem.getTouchControlLayout();
    this.joystickCenter = {
      x: this.layout.joystickX,
      y: this.layout.joystickY,
    };
    this.joystickRadius = this.layout.joystickRadius;
    this.thumbRadius = this.layout.thumbRadius;

    this.createJoystickZone();
    this.createJoystick();
    this.createActionButton();
    this.bindPointerEvents();
    this.unbindResize = ResponsiveSystem.onResize(() => this.relayout());
  }

  setVisible(visible: boolean): void {
    this.controlsVisible = visible;
    this.joystickZone?.setVisible(visible);
    this.joystickBase?.setVisible(visible);
    this.joystickThumb?.setVisible(visible);
    this.actionButton?.setVisible(visible);
    this.actionLabel?.setVisible(visible);
    if (!visible) {
      this.resetJoystick();
      this.inputSystem.setMobileInput({ action: false });
    }
  }

  private relayout(): void {
    if (!this.joystickBase || ResponsiveSystem.shouldBlockPortraitPlay()) return;

    this.layout = ResponsiveSystem.getTouchControlLayout();
    this.joystickCenter = {
      x: this.layout.joystickX,
      y: this.layout.joystickY,
    };
    this.joystickRadius = this.layout.joystickRadius;
    this.thumbRadius = this.layout.thumbRadius;

    this.joystickZone?.setPosition(this.layout.zoneX, this.layout.zoneY);
    this.joystickZone?.setSize(this.layout.zoneWidth, this.layout.zoneHeight);

    const { x, y } = this.joystickCenter;
    this.joystickBase.setPosition(x, y);
    this.joystickBase.setRadius(this.joystickRadius);
    this.joystickThumb?.setPosition(x, y);
    this.joystickThumb?.setRadius(this.thumbRadius);

    const { actionX, actionY, actionRadius } = this.layout;
    this.actionButton?.setPosition(actionX, actionY);
    this.actionButton?.setRadius(actionRadius);
    this.actionLabel?.setPosition(actionX, actionY);
    this.actionLabel?.setStyle(this.actionLabelStyle(actionRadius));

    if (this.controlsVisible) {
      this.resetJoystick();
    }
  }

  private actionLabelStyle(actionRadius: number): Phaser.Types.GameObjects.Text.TextStyle {
    return UIStyle.onBrightBg(
      actionRadius >= 46 ? UIStyle.px(28) : actionRadius >= 40 ? UIStyle.px(24) : UIStyle.px(22),
      '#ffffff',
      true,
    );
  }

  private bindPointerEvents(): void {
    this.moveHandler = (pointer: Phaser.Input.Pointer) => {
      if (this.pointerId === pointer.id) this.updateJoystick(pointer);
    };
    this.upHandler = (pointer: Phaser.Input.Pointer) => {
      if (this.pointerId === pointer.id) this.resetJoystick();
    };

    this.scene.input.on('pointermove', this.moveHandler);
    this.scene.input.on('pointerup', this.upHandler);
    this.scene.input.on('pointerupoutside', this.upHandler);
  }

  private getPointerWorld(pointer: Phaser.Input.Pointer): { x: number; y: number } {
    return { x: pointer.worldX, y: pointer.worldY };
  }

  private createJoystickZone(): void {
    this.joystickZone = this.scene.add.zone(
      this.layout.zoneX,
      this.layout.zoneY,
      this.layout.zoneWidth,
      this.layout.zoneHeight,
    );
    this.joystickZone.setScrollFactor(0);
    // Di bawah tombol menu/HUD agar UI atas kiri tetap bisa diklik
    this.joystickZone.setDepth(90);
    this.joystickZone.setInteractive({ draggable: false });

    this.joystickZone.on('pointerdown', (pointer: Phaser.Input.Pointer, _lx: number, _ly: number, event: Phaser.Types.Input.EventData) => {
      if (!this.controlsVisible) return;
      const world = this.getPointerWorld(pointer);
      if (world.y < this.layout.hudExclusionY) return;
      if (world.x > this.layout.actionX - this.layout.actionRadius - 30) return;
      event?.stopPropagation();
      this.pointerId = pointer.id;
      this.updateJoystick(pointer);
    });
  }

  private createJoystick(): void {
    const { x, y } = this.joystickCenter;

    this.joystickBase = this.scene.add.circle(x, y, this.joystickRadius, 0xffffff, 0.28);
    this.joystickBase.setStrokeStyle(3, 0x40916c, 0.9);
    this.joystickBase.setScrollFactor(0);
    this.joystickBase.setDepth(91);

    this.joystickThumb = this.scene.add.circle(x, y, this.thumbRadius, 0x40916c, 0.7);
    this.joystickThumb.setStrokeStyle(2, 0xffffff, 0.95);
    this.joystickThumb.setScrollFactor(0);
    this.joystickThumb.setDepth(92);
  }

  private updateJoystick(pointer: Phaser.Input.Pointer): void {
    if (!this.joystickThumb) return;

    const world = this.getPointerWorld(pointer);
    const dx = world.x - this.joystickCenter.x;
    const dy = world.y - this.joystickCenter.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const maxDist = this.joystickRadius - this.thumbRadius * 0.35;
    const clamped = dist > maxDist ? maxDist / dist : 1;
    const nx = (dx * clamped) / maxDist;
    const ny = (dy * clamped) / maxDist;

    this.joystickThumb.setPosition(
      this.joystickCenter.x + dx * clamped,
      this.joystickCenter.y + dy * clamped,
    );
    this.inputSystem.setMobileAnalog(nx, ny);
  }

  private resetJoystick(): void {
    this.pointerId = null;
    this.joystickThumb?.setPosition(this.joystickCenter.x, this.joystickCenter.y);
    this.inputSystem.setMobileAnalog(0, 0);
  }

  private createActionButton(): void {
    const { actionX: x, actionY: y, actionRadius: r } = this.layout;

    this.actionButton = this.scene.add.circle(x, y, r, 0xc1121f, 0.85);
    this.actionButton.setStrokeStyle(3, 0xffffff, 0.95);
    this.actionButton.setScrollFactor(0);
    this.actionButton.setDepth(91);
    this.actionButton.setInteractive();

    this.actionLabel = this.scene.add.text(x, y, 'E', {
      ...this.actionLabelStyle(r),
    }).setOrigin(0.5).setScrollFactor(0).setDepth(92);

    this.actionButton.on('pointerdown', () => this.inputSystem.setMobileInput({ action: true }));
    this.actionButton.on('pointerup', () => this.inputSystem.setMobileInput({ action: false }));
    this.actionButton.on('pointerout', () => this.inputSystem.setMobileInput({ action: false }));
  }

  destroy(): void {
    this.resetJoystick();
    this.unbindResize?.();
    this.unbindResize = undefined;
    if (this.moveHandler) this.scene.input.off('pointermove', this.moveHandler);
    if (this.upHandler) {
      this.scene.input.off('pointerup', this.upHandler);
      this.scene.input.off('pointerupoutside', this.upHandler);
    }
    this.joystickZone?.destroy();
    this.joystickBase?.destroy();
    this.joystickThumb?.destroy();
    this.actionButton?.destroy();
    this.actionLabel?.destroy();
  }
}
